import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PortalLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/portal/auth/login`, { email, password });
      localStorage.setItem("portal_token", res.data.token);
      toast.success("Login successful!");
      navigate("/portal");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="portal-login-page">
      {/* Left Decorative Panel */}
      <div className="hidden lg:flex lg:w-[45%] relative overflow-hidden items-center justify-center" style={{background: 'linear-gradient(135deg, #0046BE 0%, #0033A0 100%)'}}>
        <div className="absolute inset-0 opacity-[0.05]" style={{backgroundImage: 'radial-gradient(circle at 30% 40%, white 1px, transparent 1px)', backgroundSize: '24px 24px'}} />
        <div className="relative z-10 px-12 max-w-md">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-8" style={{background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)'}}>
            <span className="text-xl font-black text-white">F</span>
          </div>
          <h2 className="text-3xl font-bold text-white font-['Outfit'] leading-tight">Your services,<br />at a glance.</h2>
          <p className="text-blue-100/60 mt-4 text-sm leading-relaxed">Access your jobs, invoices, vehicles, and service history all in one place.</p>
          <div className="mt-10 space-y-3">
            {["View job status & schedule", "Download invoices as PDF", "Manage notification preferences"].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-white/40" />
                <span className="text-blue-100/50 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-[#f8f9fb]">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-1.5 text-slate-300 hover:text-slate-500 text-sm mb-8 transition-colors" data-testid="portal-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to main site
          </Link>

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-slate-800 font-['Outfit']">Customer Portal</h1>
            <p className="text-slate-400 mt-1.5 text-sm">Sign in to view your jobs and invoices</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                <Input
                  type="email" placeholder="you@example.com" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 pl-10 rounded-xl border-slate-200 focus:border-[#0046BE] focus:ring-[#0046BE]/10 text-slate-700 placeholder:text-slate-300"
                  required data-testid="portal-email-input"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 font-medium">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300" />
                <Input
                  type={showPassword ? "text" : "password"} placeholder="Enter your password" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 pl-10 pr-10 rounded-xl border-slate-200 focus:border-[#0046BE] focus:ring-[#0046BE]/10 text-slate-700 placeholder:text-slate-300"
                  required data-testid="portal-password-input"
                />
                <button type="button" className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-400 transition-colors" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full h-11 rounded-xl bg-[#0046BE] hover:bg-[#0033A0] text-white font-medium text-sm mt-2" disabled={loading} data-testid="portal-login-btn">
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-400 text-sm">
              Don't have an account?{" "}
              <Link to="/portal/register" className="text-[#0046BE] hover:text-[#0033A0] font-medium transition-colors">Register</Link>
            </p>
          </div>

          <p className="text-center text-slate-300 text-xs mt-8">
            Powered by <span className="font-medium text-slate-400">Fieldz AI</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PortalLogin;
